import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Toast, useToast } from '@zillow/constellation-10';

import landlordNotifications from '@rental-manager/notifications';
import { closeNotification, stackedCloseNotification } from '@common/actions/NotificationActions';
import NotificationContainer from '@common/components/NotificationContainer';
import StackedNotificationsContainer from '@common/components/StackedNotificationsContainer';
import notificationTypes from '@common/constants/notificationTypes';
import userPersonas from '@common/constants/userPersonas';

const TONES: Record<string, 'critical' | 'success' | 'info'> = {
  [notificationTypes.ERROR]: 'critical',
  [notificationTypes.SUCCESS]: 'success',
  [notificationTypes.INFO]: 'info',
};

type NotificationState = {
  show?: boolean;
  name?: string | null;
  extraArgs?: Record<string, string>;
};

type NotificationDefinition = {
  custom?: boolean;
  type?: string;
  text?: (args: Record<string, string>) => string;
};

const simpleDefinitionFor = (entry?: NotificationState): NotificationDefinition | undefined => {
  if (!entry?.show || !entry.name) {
    return undefined;
  }
  const definition = (landlordNotifications as Record<string, NotificationDefinition>)[entry.name];
  return definition && !definition.custom && definition.text ? definition : undefined;
};

const isCustom = (entry?: NotificationState): boolean =>
  Boolean(
    entry?.show && entry.name && (landlordNotifications as Record<string, NotificationDefinition>)[entry.name]?.custom,
  );

/**
 * Bridges the legacy redux notification state to Constellation Toasts inside the wizard shell,
 * so screens keep dispatching showModalNotification unchanged. Custom notification templates
 * (links, rich content) still render through the legacy containers.
 */
const WizardShellToasts = (): JSX.Element => {
  const dispatch = useDispatch();
  const { enqueueToast } = useToast();
  const single = useSelector(
    (state: { payments?: { landlord?: { notification?: { modal?: NotificationState } } } }) =>
      state?.payments?.landlord?.notification?.modal,
  );
  const stacked = useSelector(
    (state: { payments?: { landlord?: { stackedNotifications?: { modal?: Record<string, NotificationState> } } } }) =>
      state?.payments?.landlord?.stackedNotifications?.modal,
  );

  useEffect(() => {
    const definition = simpleDefinitionFor(single);
    if (!definition?.text) {
      return;
    }
    enqueueToast(
      <Toast tone={definition.type ? TONES[definition.type] : undefined}>
        {definition.text({ ...single?.extraArgs })}
      </Toast>,
    );
    dispatch(closeNotification());
  }, [single, dispatch, enqueueToast]);

  useEffect(() => {
    Object.entries(stacked ?? {}).forEach(([key, entry]) => {
      const definition = simpleDefinitionFor(entry);
      if (!definition?.text) {
        return;
      }
      enqueueToast(
        <Toast tone={definition.type ? TONES[definition.type] : undefined}>
          {definition.text({ ...entry.extraArgs })}
        </Toast>,
      );
      dispatch(stackedCloseNotification(key));
    });
  }, [stacked, dispatch, enqueueToast]);

  const hasCustomStacked = Object.values(stacked ?? {}).some(isCustom);

  return (
    <>
      {isCustom(single) && <NotificationContainer modal persona={userPersonas.LANDLORD} />}
      {hasCustomStacked && <StackedNotificationsContainer modal persona={userPersonas.LANDLORD} />}
    </>
  );
};

export default WizardShellToasts;
